import React, { useMemo, useState } from 'react';
import { Typography, Collapse, Tag, Space, Input, Alert, Divider, Button, theme } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { INTEGRATION_ENDPOINTS, EndpointDoc, EndpointParam, HttpMethod } from '../api/integrationEndpoints';

const { Title, Paragraph, Text } = Typography;

// Reference prose is read line by line: past ~80 characters the eye loses its
// place on the way back to the left margin. The content column here is capped
// well below the available width on purpose — this page is documentation, not
// a table that wants every pixel.
const PROSE_WIDTH = 820;

// Typography renders `code` at 85% of its own font size, so 14px lands on
// ~12px — the floor for readable text. Smaller values drop it under 11px.
const CODE: React.CSSProperties = { fontSize: 14 };

// Method → Tag color map. Mirrors common REST conventions (green GET, blue POST,
// orange PUT, purple PATCH, red DELETE) so operators can scan the list visually.
const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  PATCH: 'purple',
  DELETE: 'red',
};

const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => {
  const { token } = theme.useToken();
  return (
    <pre
      style={{
        margin: 0,
        padding: 8,
        fontSize: 12,
        lineHeight: 1.5,
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        overflow: 'auto',
        maxHeight: 280,
        maxWidth: PROSE_WIDTH,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

const ParamsTable: React.FC<{ params: EndpointParam[]; title: string }> = ({ params, title }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ marginBottom: 10 }}>
      <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>{title}</Text>
      <table style={{ width: '100%', maxWidth: PROSE_WIDTH, fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: token.colorTextSecondary }}>
            <th style={{ textAlign: 'left', paddingRight: 12, fontWeight: 500, width: 140 }}>Name</th>
            <th style={{ textAlign: 'left', paddingRight: 12, fontWeight: 500, width: 80 }}>Required</th>
            <th style={{ textAlign: 'left', fontWeight: 500 }}>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={`${p.in}-${p.name}`}>
              <td style={{ paddingRight: 12, paddingTop: 2, verticalAlign: 'top' }}>
                <Text code style={CODE}>{p.name}</Text>
                {p.example && <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>e.g. {p.example}</Text>}
              </td>
              <td style={{ paddingRight: 12, paddingTop: 2, verticalAlign: 'top' }}>
                {p.required
                  ? <Tag color="red" style={{ margin: 0, fontSize: 12, lineHeight: '18px' }}>required</Tag>
                  : <Text type="secondary">optional</Text>}
              </td>
              <td style={{ paddingTop: 2, verticalAlign: 'top' }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EndpointCard: React.FC<{ e: EndpointDoc }> = ({ e }) => {
  const pathParams = (e.params || []).filter((p) => p.in === 'path');
  const queryParams = (e.params || []).filter((p) => p.in === 'query');

  return (
    <div>
      {e.description && (
        <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 13, maxWidth: PROSE_WIDTH }}>
          {e.description}
        </Paragraph>
      )}

      <div style={{ marginBottom: 8 }}>
        <Tag color="geekblue" style={{ fontSize: 12 }}>{e.permission}</Tag>
        <Tag color={e.successStatus === 201 ? 'cyan' : 'green'} style={{ fontSize: 12 }}>
          {e.successStatus || 200} on success
        </Tag>
      </div>

      {pathParams.length > 0 && <ParamsTable params={pathParams} title="Path parameters" />}
      {queryParams.length > 0 && <ParamsTable params={queryParams} title="Query parameters" />}

      {e.requestExample !== undefined && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Request body</Text>
          <JsonBlock value={e.requestExample} />
        </div>
      )}

      {e.responseExample !== undefined && (
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Response (inside <code>data</code>)</Text>
          <JsonBlock value={e.responseExample} />
        </div>
      )}

      {e.errorStatuses && e.errorStatuses.length > 0 && (
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Error codes</Text>
          <Space direction="vertical" size={2} style={{ display: 'flex' }}>
            {e.errorStatuses.map((err) => (
              <Text key={err.code} type="secondary" style={{ fontSize: 12 }}>
                <Text code style={CODE}>{err.code}</Text> {err.description}
              </Text>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};

const IntegrationDocsPage: React.FC = () => {
  const [filter, setFilter] = useState('');

  // Filter matches method, path, summary, or group — lets operators jump to
  // "delete proxy" or "reports" without scrolling through the full catalog.
  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return INTEGRATION_ENDPOINTS;
    return INTEGRATION_ENDPOINTS.filter((e) =>
      [e.method, e.path, e.summary, e.group, e.description || ''].some((s) =>
        s.toLowerCase().includes(needle),
      ),
    );
  }, [filter]);

  const grouped = useMemo(() => {
    const acc: Record<string, EndpointDoc[]> = {};
    for (const e of filtered) {
      (acc[e.group] ||= []).push(e);
    }
    return acc;
  }, [filtered]);

  return (
    <>
      <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>Integration API</Title>
      <Paragraph type="secondary" style={{ marginBottom: 10, fontSize: 13, maxWidth: PROSE_WIDTH }}>
        External integrations authenticate with <Text code style={CODE}>X-API-Key</Text> header and call the
        <Text code style={CODE}>/api/integration/v1/*</Text> endpoints listed below. Create keys on the{' '}
        <a href="/api-keys">API Keys</a> page.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 10, maxWidth: PROSE_WIDTH, padding: '6px 12px' }}
        message={<Text strong style={{ fontSize: 13 }}>Quick facts</Text>}
        description={(
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, lineHeight: 1.6 }}>
            <li>Pagination is <Text code style={CODE}>skip</Text> + <Text code style={CODE}>limit</Text>; no <Text code style={CODE}>total</Text> is returned. Iterate until <Text code style={CODE}>hasMore</Text> is false.</li>
            <li>Tokens with a <Text strong>source filter</Text> silently see only their own servers. 404 covers both "missing" and "hidden".</li>
            <li>Every server endpoint also works by <Text strong>your own id</Text>: <Text code style={CODE}>/servers/by-source/{'{sourceId}'}</Text> — no need to store our keys.</li>
            <li><Text code style={CODE}>/reports/{'{id}'}/failures</Text> is the hourly "what broke" list — only failures, frozen when the report was made. For one server&apos;s full current picture use <Text code style={CODE}>/servers/{'{key}'}/health</Text>.</li>
            <li>Every response is <Text code style={CODE}>{'{ "success": bool, "data": ..., "error": "..." }'}</Text>.</li>
            <li>See the full markdown reference in <Text code style={CODE}>docs/INTEGRATION_API.md</Text>.</li>
          </ul>
        )}
      />

      {/* The Postman block is setup instructions, not something an operator
          re-reads daily — it collapses so the endpoint catalogue starts near
          the top of the viewport. */}
      <Collapse
        size="small"
        style={{ marginBottom: 10, maxWidth: PROSE_WIDTH }}
        items={[{
          key: 'postman',
          label: (
            <Space size={8}>
              <Text strong style={{ fontSize: 13 }}>Postman collection — sandbox</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>import, variables, sandbox token</Text>
            </Space>
          ),
          extra: (
            <Button
              size="small"
              type="primary"
              icon={<DownloadOutlined />}
              href="/integration-api.postman_collection.json"
              download="proxy-network-diag.integration-api.postman_collection.json"
              onClick={(ev) => ev.stopPropagation()}
            >
              Download
            </Button>
          ),
          children: (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <Paragraph style={{ marginBottom: 6, fontSize: 13 }}>
                Ready-to-import Postman v2.1 collection. All endpoints are included with realistic example
                responses — Postman's <Text strong>Mock Servers</Text> feature uses them to return fake data if
                you want to demo the API shape without hitting a real backend.
              </Paragraph>
              <Paragraph style={{ marginBottom: 4, fontSize: 13 }}>
                After import, set the two collection variables:
              </Paragraph>
              <ul style={{ margin: 0, marginBottom: 6, paddingLeft: 16 }}>
                <li><Text code style={CODE}>baseUrl</Text> — your gateway URL, or a Postman Mock Server URL.</li>
                <li><Text code style={CODE}>apiKey</Text> — your <Text strong>sandbox token</Text>.</li>
              </ul>
              <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                To provision the sandbox token: open <Text strong><a href="/api-keys">API Keys</a></Text> →
                Create → name it <Text code style={CODE}>sandbox</Text> → grant both integration permissions → set
                <Text code style={CODE}> Rate limit = 10</Text> requests/minute. Share the one-time-revealed key with the
                integrator. If it leaks, hit <Text strong>Regenerate</Text> to mint a new value.
              </Paragraph>
            </div>
          ),
        }]}
      />

      <Input.Search
        allowClear
        size="small"
        placeholder="Filter by method, path, group…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 10, maxWidth: 320 }}
      />

      {Object.keys(grouped).length === 0 && (
        <Paragraph type="secondary" style={{ fontSize: 13 }}>No endpoints match this filter.</Paragraph>
      )}

      {Object.entries(grouped).map(([group, endpoints]) => (
        <div key={group} style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain style={{ margin: '8px 0', fontSize: 12 }}>{group}</Divider>
          <Collapse
            size="small"
            items={endpoints.map((e) => ({
              key: e.id,
              label: (
                <Space size={6}>
                  <Tag color={METHOD_COLORS[e.method]} style={{ margin: 0, fontFamily: 'monospace', fontSize: 12, minWidth: 52, textAlign: 'center' }}>
                    {e.method}
                  </Tag>
                  <Text code style={CODE}>{e.path}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>— {e.summary}</Text>
                </Space>
              ),
              children: <EndpointCard e={e} />,
            }))}
          />
        </div>
      ))}
    </>
  );
};

export default IntegrationDocsPage;
